import {ImportingDataTypes} from "../types";

export const CATALOG_GROUP_ORDER = [
    'products',
    'technicalProperties',
    'inventory',
    'customers',
    'vendors',
    'shipping',
    'systemSettings',
];

/**
 * @param {string} groupKey
 * @returns {number}
 */
export const getCatalogGroupSortIndex = (groupKey) => {
    const idx = CATALOG_GROUP_ORDER.indexOf(groupKey);
    return idx === -1 ? CATALOG_GROUP_ORDER.length : idx;
};

/**
 * @param {Array<{id: string, title: string, group?: string, groupTitle?: string, groups?: Array}>} options
 * @returns {Array}
 */
const sortOptionsByCatalogGroup = (options) => [...options].sort((a, b) => {
    const groupA = a.groups?.[0]?.group ?? a.group ?? '';
    const groupB = b.groups?.[0]?.group ?? b.group ?? '';
    const groupDiff = getCatalogGroupSortIndex(groupA) - getCatalogGroupSortIndex(groupB);
    return groupDiff;
});

/**
 * @param {Array<{id: string, title: string}>} [_allIndustries]
 * @returns {Array<{id: string, title: string, group: string, groupTitle: string}>}
 */
export const buildDataSourceOptions = (_allIndustries = []) => [
    // ── Products: identity ────────────────────────────────────────────────────
    {
        id: ImportingDataTypes.partDescription,
        title: 'Product Description',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partTitle,
        title: 'Product Title',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partShortDescription,
        title: 'Product Short Description',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partNotes,
        title: 'Product Notes',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partUPC,
        title: 'Product UPC',
        group: 'products',
        groupTitle: 'Products',
    },
    // ── Products: visibility & flags ──────────────────────────────────────────
    {
        id: ImportingDataTypes.partVisibility,
        title: 'Product Visibility',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partSerializability,
        title: 'Product Serializability',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partDeliveryMode,
        title: 'Product Delivery Mode',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partOnlineOrderEligibility,
        title: 'Product Online Order Eligibility',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.productVisibilityToOtherPartners,
        title: 'Product Partner Visibility',
        group: 'products',
        groupTitle: 'Products',
    },
    // ── Products: commercial & extended data ──────────────────────────────────
    {
        id: ImportingDataTypes.partPriceLevels,
        title: 'Product Prices',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.productDimensions,
        title: 'Product Dimensions',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.partTechnicalInformation,
        title: "Products' Technical Information",
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.productVendors,
        title: "Product Vendors",
        groups: [
            {group: 'products', groupTitle: 'Products'},
            {group: 'vendors', groupTitle: 'Vendors'},
        ],
    },
    // ── Products: assignments ───────────────────────────────────────────────────
    {
        id: ImportingDataTypes.productTagAssignment,
        title: 'Product Tags Assignment',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.productCategoryAssignment,
        title: 'Product Category Assignment',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.productFormAssignment,
        title: 'Product Forms Assignment',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.productRelationAssignment,
        title: 'Product Relation Assignment',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.productCodeAssignment,
        title: 'Product Codes Assignment',
        group: 'products',
        groupTitle: 'Products',
    },
    // ── Products: catalog import ──────────────────────────────────────────────
    {
        id: ImportingDataTypes.productsFromAnotherPartner,
        title: 'Products From Another Partner',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.externalProducts,
        title: 'External Products',
        group: 'products',
        groupTitle: 'Products',
    },
    {
        id: ImportingDataTypes.removeProduct,
        title: 'Remove Products',
        group: 'products',
        groupTitle: 'Products',
    },
    // ── Technical properties ──────────────────────────────────────────────────
    {
        id: ImportingDataTypes.productProperties,
        title: 'Product Property Definitions',
        group: 'technicalProperties',
        groupTitle: 'Technical properties',
    },
    {
        id: ImportingDataTypes.productPropertyOptions,
        title: 'Product Property Options',
        group: 'technicalProperties',
        groupTitle: 'Technical properties',
    },
    // ── Inventory ─────────────────────────────────────────────────────────────
    {
        id: ImportingDataTypes.warehouseAlertThresholds,
        title: 'Warehouse Alert Thresholds',
        group: 'inventory',
        groupTitle: 'Inventory',
    },
    {
        id: ImportingDataTypes.productWarehouseAssignment,
        title: 'Product Warehouse Assignment',
        group: 'inventory',
        groupTitle: 'Inventory',
    },
    {
        id: ImportingDataTypes.setInventoryAvailability,
        title: 'Inventory Availability',
        group: 'inventory',
        groupTitle: 'Inventory',
    },
    // ── Customers ─────────────────────────────────────────────────────────────
    {
        id: ImportingDataTypes.customersTerritoriesAndAccountManagers,
        title: 'Customer Territories and Account Managers',
        group: 'customers',
        groupTitle: 'Customers',
    },
    {
        id: ImportingDataTypes.externalCustomers,
        title: 'External Customers',
        group: 'customers',
        groupTitle: 'Customers',
    },
    // ── Vendors ───────────────────────────────────────────────────────────────
    {
        id: ImportingDataTypes.externalVendors,
        title: 'External Vendors',
        group: 'vendors',
        groupTitle: 'Vendors',
    },
    // ── Shipping ──────────────────────────────────────────────────────────────
    {
        id: ImportingDataTypes.productFlatShippingRates,
        title: 'Product Flat Shipping Rates',
        group: 'shipping',
        groupTitle: 'Shipping',
    },
    // ── System settings ───────────────────────────────────────────────────────
    {
        id: ImportingDataTypes.priceMatrices,
        title: 'Price Matrices',
        group: 'systemSettings',
        groupTitle: 'System Settings',
    },
];

/**
 * @param {Array} options
 * @param {Object} operatorAccessLevels
 * @returns {Array}
 */
export const filterDataSourceOptionsByAccess = (options, operatorAccessLevels) => {
    const res = options.filter(option => {
        switch (option.id) {
            case ImportingDataTypes.partDescription:
            case ImportingDataTypes.partTitle:
            case ImportingDataTypes.partShortDescription:
            case ImportingDataTypes.partUPC:
            case ImportingDataTypes.partVisibility:
            case ImportingDataTypes.partDeliveryMode:
            case ImportingDataTypes.partOnlineOrderEligibility:
            case ImportingDataTypes.productVisibilityToOtherPartners:
                return operatorAccessLevels.packages.lookup.editProductBasicInformation;
            case ImportingDataTypes.partNotes:
                return operatorAccessLevels.packages.lookup.editProductNotes;
            case ImportingDataTypes.priceMatrices:
                return operatorAccessLevels.packages.sales.managePriceMatrices;
            case ImportingDataTypes.partPriceLevels:
                return operatorAccessLevels.packages.lookup.editProductPricing;
            case ImportingDataTypes.customersTerritoriesAndAccountManagers:
                return operatorAccessLevels.packages.sales.importAndExportTerritoriesAndAccountManagers;
            case ImportingDataTypes.warehouseAlertThresholds:
                return operatorAccessLevels.packages.sales.manageWarehouseSchedules;
            case ImportingDataTypes.productWarehouseAssignment:
                return operatorAccessLevels.packages.lookup.products;
            case ImportingDataTypes.setInventoryAvailability:
                return operatorAccessLevels.packages.bizInventory.setInventoryAvailability;
            case ImportingDataTypes.removeProduct:
                return operatorAccessLevels.packages.lookup.removeProduct;
            case ImportingDataTypes.productsFromAnotherPartner:
                return operatorAccessLevels.packages.base.partnershipSettings;
            case ImportingDataTypes.productDimensions:
                return operatorAccessLevels.packages.lookup.editProductDimensions;
            case ImportingDataTypes.productVendors:
                return operatorAccessLevels.packages.base.manageVendorProducts;
            case ImportingDataTypes.externalCustomers:
                return operatorAccessLevels.packages.base.importExternalCustomer;
            case ImportingDataTypes.externalVendors:
                return operatorAccessLevels.packages.base.importExternalVendor;
            case ImportingDataTypes.productCategoryAssignment:
                return operatorAccessLevels.packages.lookup.bulkProductCategoryAssignment;
            case ImportingDataTypes.productFormAssignment:
                return operatorAccessLevels.packages.lookup.editProductBasicInformation;
            case ImportingDataTypes.productRelationAssignment:
                return operatorAccessLevels.packages.lookup.editProductRelations;
            case ImportingDataTypes.productCodeAssignment:
                return operatorAccessLevels.packages.lookup.editProductCodes;
            case ImportingDataTypes.partTechnicalInformation:
            case ImportingDataTypes.productProperties:
            case ImportingDataTypes.productPropertyOptions:
                return operatorAccessLevels.packages.lookup.editProductTechnicalInformation;
            case ImportingDataTypes.externalProducts:
                return operatorAccessLevels.packages.lookup.importExternalProduct;
            case ImportingDataTypes.productTagAssignment:
                return operatorAccessLevels.packages.lookup.manageTags;
            case ImportingDataTypes.productFlatShippingRates:
                return operatorAccessLevels.packages.sales.managePartRates;
            default:
                return true;
        }
    });
    return sortOptionsByCatalogGroup(res);
};

/**
 * @param {Array} options
 * @param {{expandMemberships?: boolean}} [config]
 * @returns {Array<{group: string, groupTitle: string, options: Array}>}
 */
export const groupDataSourceOptions = (options, config = {}) => {
    const expandMemberships = config.expandMemberships !== false;
    const groups = new Map();
    for (const option of options) {
        const memberships = Array.isArray(option.groups) && option.groups.length
            ? option.groups
            : [{group: option.group, groupTitle: option.groupTitle}];
        const resolvedMemberships = expandMemberships
            ? memberships
            : [memberships[0]].filter(Boolean);
        for (const membership of resolvedMemberships) {
            if (!membership?.group) {
                continue;
            }
            if (!groups.has(membership.group)) {
                groups.set(membership.group, {
                    group: membership.group,
                    groupTitle: membership.groupTitle ?? membership.group,
                    options: [],
                });
            }
            const bucket = groups.get(membership.group);
            if (bucket.options.some((existing) => existing.id === option.id)) {
                continue;
            }
            bucket.options.push(option);
        }
    }
    return [...groups.values()].sort(
        (a, b) => getCatalogGroupSortIndex(a.group) - getCatalogGroupSortIndex(b.group),
    );
};
